<template>
  <div
    class="tournament-player-tile"
    :class="{
      'is-clickable': !isAbsent,
      'is-draggable': isDraggable && !isAbsent,
    }"
    :draggable="isDraggable && !isAbsent"
    @click="handleClick"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <b-tooltip position="is-bottom" type="is-white" :delay="100">
      <div
        class="tile-content box"
        :class="[tileColorClass, { 'is-selected': isCurrent }]"
      >
        <div
          class="tile-inner is-flex"
          ref="playerTile"
          @mouseenter="showModal(1000)"
          @mouseleave="stopModalOpen"
        >
          <!-- Avatar on the left -->
          <div class="avatar-container">
            <Avatar :player="playerUser" />
          </div>

          <!-- Content on the right -->
          <div class="content-container is-flex-grow-1">
            <!-- Line 1: Player name (left) and score (right) -->
            <div class="is-flex is-justify-content-space-between">
              <span class="player-name" :title="playerName">{{
                truncatedPlayerName
              }}</span>
              <span class="player-score">
                <template v-if="showScoreProgression">
                  <span>{{ playerScore }}</span>
                  <span>➝</span>
                  <span :class="newScoreClass">{{ newPlayerScore }}</span>
                </template>
                <template v-else>
                  {{ playerScore }}
                </template>
              </span>
            </div>

            <!-- Line 2: Rank (left) and colorPref (right) -->
            <div class="is-flex is-justify-content-space-between">
              <span class="player-rank">Rank: {{ playerRank }}</span>
              <span class="player-color-pref">{{ playerColorPref }}</span>
            </div>
          </div>
        </div>

        <!-- Close button (show on hover only) -->
        <button
          v-if="canClose"
          class="delete is-small close-button"
          @click.stop="handleClose"
          title="Remove player"
        ></button>
      </div>

      <template v-slot:content>
        <p>
          <span v-if="isAbsent">absent</span>
          <span v-else-if="isPlayed">played before</span>
          <span v-else-if="isPaired">paired</span>
          <span v-else>available</span>
        </p>
      </template>
    </b-tooltip>
  </div>
</template>

<script>
import Avatar from "@/components/players/Avatar.vue";
import { EventBus } from "@/services/event-bus";
import {
  TOURNAMENT_GAME_RESULT,
  TOURNAMENT_GAME_RESULT_SCORE,
} from "vue-chess-shared/constants";

export default {
  name: "TournamentPlayerTile",

  components: {
    Avatar,
  },

  props: {
    // Player standing data from round.standings
    standing: {
      type: Object,
      required: true,
      validator: (value) => {
        return value && value.player && typeof value.matchPoints === "number";
      },
    },

    // Current selection state
    selectedUsername: {
      type: String,
      default: null,
    },

    // Pairing status flags
    isAbsent: {
      type: Boolean,
      default: false,
    },

    isPlayed: {
      type: Boolean,
      default: false,
    },

    isPaired: {
      type: Boolean,
      default: false,
    },

    // Color preference string
    colorPref: {
      type: String,
      default: "+0",
    },

    // Draggable functionality (for future drag-and-drop)
    isDraggable: {
      type: Boolean,
      default: false,
    },

    // Game result for score progression (enum value from TOURNAMENT_GAME_RESULT)
    gameResult: {
      type: Number,
      default: null,
    },

    // Tournament info for portal
    totalRounds: {
      type: Number,
      default: null,
    },

    roundNumber: {
      type: Number,
      default: null,
    },

    // Full player object with participation status
    fullPlayer: {
      type: Object,
      default: null,
    },

    // Custom drag data type (for game players vs tile players)
    dragDataType: {
      type: String,
      default: "player",
    },

    // Game ID for game-player drag operations
    gameId: {
      type: String,
      default: null,
    },

    // Color for game-player drag operations
    color: {
      type: String,
      default: null,
    },

    // Show close button
    canClose: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      focused: false,
      showTimeout: null,
      portalPosition: { top: 0, left: 0 },
    };
  },

  computed: {
    playerName() {
      return (
        (
          this.standing.player.user?.first_name +
          " " +
          this.standing.player.user?.last_name
        ).trim() || this.standing.player.id
      );
    },

    truncatedPlayerName() {
      const maxLength = 15; // Adjust this value as needed
      if (this.playerName.length <= maxLength) {
        return this.playerName;
      }
      return this.playerName.substring(0, maxLength - 3) + "...";
    },

    playerUsername() {
      return this.standing.player.id;
    },

    playerUser() {
      return this.standing.player.user || { username: this.standing.player.id };
    },

    playerScore() {
      return this.standing.matchPoints;
    },

    // Whether to show progression (current->new) based on game result
    showScoreProgression() {
      return (
        this.gameResult !== undefined &&
        this.gameResult !== null &&
        this.gameResult !== TOURNAMENT_GAME_RESULT.UNFINISHED
      );
    },

    // Calculate the new score after this game's result for this player
    newPlayerScore() {
      if (!this.showScoreProgression) return this.playerScore;
      const delta = TOURNAMENT_GAME_RESULT_SCORE[this.gameResult] || 0;
      return (this.playerScore || 0) + delta;
    },

    // Class for coloring the new score
    newScoreClass() {
      if (!this.showScoreProgression) return "";

      const delta = TOURNAMENT_GAME_RESULT_SCORE[this.gameResult] || 0;

      if (delta === 1) {
        return "has-text-info";
      }
      if (delta === 0) {
        return "has-text-danger";
      }
      // Draw or neutral cases (FORFEITD etc) use default text color
      return "has-text-primary";
    },

    playerRank() {
      // Prefer rank from standing; fallback to fullPlayer.currentRank if provided
      const rank =
        this.standing.rank ?? (this.fullPlayer && this.fullPlayer.currentRank);
      return rank !== undefined && rank !== null ? rank : "-";
    },

    playerColorPref() {
      return this.colorPref;
    },

    isCurrent() {
      return this.selectedUsername === this.playerUsername;
    },

    isTarget() {
      return (
        this.selectedUsername && this.selectedUsername !== this.playerUsername
      );
    },

    tileColorClass() {
      let colorClass = "";

      if (this.isAbsent) {
        colorClass = "has-background-dark";
      } else if (this.isPaired) {
        colorClass = "has-background-warning";
      } else colorClass = "has-background-success";

      // Color scheme matching existing TournamentRoundPairingStats
      if (this.isCurrent) {
        colorClass += " has-text-white";
      } else if (this.isTarget) {
        if (this.isPlayed) {
          colorClass = "has-background-danger";
        }
        colorClass += " has-text-white";
      }

      return colorClass;
    },
  },

  methods: {
    showModal(ms) {
      this.focused = true;

      // Clear any existing timeout
      clearTimeout(this.showTimeout);

      // Calculate position for the portal
      this.calculatePortalPosition();

      // Only show the modal if the cursor stays for the specified time
      this.showTimeout = setTimeout(() => {
        // Only proceed if we're still focused (cursor hasn't left)
        if (this.focused) {
          // Show the shared portal with this player's data
          // Use fullPlayer if available (has participation status), otherwise fallback to standing.player
          const playerData = this.fullPlayer || {
            ...this.standing.player,
            user: this.playerUser,
            colorPref: this.playerColorPref,
            score: this.playerScore,
          };

          EventBus.$emit("show-player-portal", {
            player: playerData,
            totalRounds: this.totalRounds,
            roundNumber: this.roundNumber,
            position: this.portalPosition,
          });
        }
      }, ms);
    },

    calculatePortalPosition() {
      if (!this.$refs.playerTile) return;

      const rect = this.$refs.playerTile.getBoundingClientRect();
      this.portalPosition = {
        top: rect.top,
        left: rect.left,
      };
    },

    stopModalOpen() {
      this.focused = false;
      clearTimeout(this.showTimeout);
    },

    handleClick() {
      if (this.isAbsent) {
        return;
      }

      this.$emit("player-selected", this.playerUsername);
    },

    handleDragStart(event) {
      // Close the SharedPlayerTagPortal when starting drag
      this.stopModalOpen();
      EventBus.$emit("hide-player-portal");

      // Build drag data object
      const dragData = {
        type: this.dragDataType,
        username: this.playerUsername,
        playerName: this.playerName,
      };

      // Add game-specific data if available (for game-player tiles)
      if (this.gameId) {
        dragData.gameId = this.gameId;
      }
      if (this.color) {
        dragData.color = this.color;
      }

      // Set drag data
      event.dataTransfer.setData("text/plain", JSON.stringify(dragData));
      event.dataTransfer.effectAllowed = "move";

      // Add visual feedback
      event.target.style.opacity = "0.5";
    },

    handleDragEnd(event) {
      // Remove visual feedback
      event.target.style.opacity = "1";
    },

    handleClose() {
      this.$emit("close");
    },
  },

  beforeDestroy() {
    // Clean up timeouts
    clearTimeout(this.showTimeout);
  },
};
</script>

<style scoped>
.tournament-player-tile {
  margin: 0.25rem;
  display: inline-block;
}

.tile-content {
  padding: 0.5rem;
  min-width: 230px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.tile-content.is-selected {
  border: 3px solid #3273dc;
  box-shadow: 0 0 0 1px #3273dc;
}

.tile-inner {
  align-items: center;
  gap: 0.75rem;
}

.avatar-container {
  width: 36px;
  height: auto;
  flex-shrink: 0;
}

.content-container {
  min-width: 0; /* Allow text to truncate if needed */
}

.player-name {
  font-size: 1rem;
  font-weight: 700;
}

.player-score {
  font-size: 1rem;
  font-weight: 600;
}

.player-rank,
.player-color-pref {
  font-size: 0.75rem;
  opacity: 0.8;
}

.is-clickable {
  cursor: pointer;
  user-select: none;
}

.is-clickable:hover .tile-content {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Future drag-and-drop functionality will be added here */

/* Ensure proper spacing and alignment */
.tag.tile-content {
  display: block;
  white-space: normal;
  padding: 0.5rem 0.75rem;
  line-height: 1.2;
}

/* Close button styling */
.close-button {
  position: absolute;
  top: -5px;
  right: -5px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
}

.tournament-player-tile:hover .close-button {
  opacity: 1;
}
</style>
